import { h } from "@/lib/dom";
import { button, openModal } from "@/components/ui";

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;

// Ohne explizite Größe kollabiert das geklonte <svg> im Flex-Container auf 0.
function naturalSize(source: SVGElement): { width: number; height: number } | null {
  const viewBox = source.getAttribute("viewBox");
  const parts = viewBox?.trim().split(/[\s,]+/).map(Number);
  if (parts?.length === 4 && parts[2] > 0 && parts[3] > 0) return { width: parts[2], height: parts[3] };
  const rect = source.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null;
}

function buildViewer(source: SVGElement): HTMLElement {
  const svg = source.cloneNode(true) as SVGElement;
  const size = naturalSize(source) ?? { width: 800, height: 600 };
  svg.setAttribute("width", String(size.width));
  svg.setAttribute("height", String(size.height));
  svg.style.maxWidth = "none";
  svg.style.display = "block";

  let scale = 1;
  let x = 0;
  let y = 0;
  const stage = h("div", { class: "diagram-popup-stage" }, svg);
  const viewport = h("div", { class: "diagram-popup-viewport" }, stage);
  const clamp = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  const apply = () => {
    stage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  };
  const fitScale = () =>
    clamp(Math.min(4, viewport.clientWidth / size.width, viewport.clientHeight / size.height) * 0.95);
  const fit = () => {
    scale = fitScale();
    x = 0;
    y = 0;
    apply();
  };
  const zoomBy = (factor: number) => {
    const next = clamp(scale * factor);
    x *= next / scale;
    y *= next / scale;
    scale = next;
    apply();
  };

  const pointers = new Map<number, { x: number; y: number }>();
  let pinch: { dist: number; scale: number; midX: number; midY: number; x: number; y: number } | null = null;
  const twoFinger = () => {
    const [a, b] = [...pointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2 };
  };

  viewport.addEventListener("pointerdown", (e) => {
    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      /* synthetischer oder schon beendeter Zeiger */
    }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) pinch = { ...twoFinger(), scale, x, y };
  });
  viewport.addEventListener("pointermove", (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.set(e.pointerId, cur);
    if (pinch && pointers.size === 2) {
      const now = twoFinger();
      const next = clamp(pinch.scale * (now.dist / pinch.dist));
      x = pinch.x * (next / pinch.scale) + (now.midX - pinch.midX);
      y = pinch.y * (next / pinch.scale) + (now.midY - pinch.midY);
      scale = next;
    } else if (pointers.size === 1) {
      x += cur.x - prev.x;
      y += cur.y - prev.y;
    }
    apply();
  });
  const release = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
  };
  viewport.addEventListener("pointerup", release);
  viewport.addEventListener("pointercancel", release);
  viewport.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      zoomBy(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)));
    },
    { passive: false }
  );
  viewport.addEventListener("dblclick", () => {
    const fitted = fitScale();
    if (Math.abs(scale - fitted) < 0.01) zoomBy(2.5);
    else fit();
  });

  requestAnimationFrame(fit);

  return h(
    "div",
    { class: "diagram-popup" },
    viewport,
    h(
      "div",
      { class: "diagram-popup-tools" },
      button({ icon: "minus", variant: "ghost", size: "sm", title: "Verkleinern", onClick: () => zoomBy(1 / 1.4) }),
      button({ label: "Einpassen", variant: "ghost", size: "sm", onClick: fit }),
      button({ icon: "plus", variant: "ghost", size: "sm", title: "Vergrößern", onClick: () => zoomBy(1.4) })
    )
  );
}

export function bindDiagramPopups(root: HTMLElement): void {
  if (root.dataset.diagramsBound) return;
  root.dataset.diagramsBound = "1";
  root.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>(".md-mermaid, .md-tikz");
    if (!el || !root.contains(el)) return;
    const svg = el.querySelector("svg");
    if (!svg) return;
    e.preventDefault();
    openModal({ title: "Diagramm", body: buildViewer(svg), wide: true, guardUnsaved: false });
  });
}
